import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import InvoiceList from './InvoiceList';
import PaymentHistory from './PaymentHistory';
import PaymentMethodSelector from './PaymentMethodSelector';
import InvoiceDetail from './InvoiceDetail';
import PaymentPlanCard from './PaymentPlanCard';
import BillingStats from './BillingStats';
import OverdueAlerts from './OverdueAlerts';
import StripePayment from '../StripePayment';
import './BillingDashboard.css';

interface PaymentPlan {
  id: string;
  plan_type: string;
  plan_status: string;
  billing_cycle: string;
  start_date: string;
  end_date: string;
  total_amount: number;
  monthly_amount?: number;
  installment_count?: number;
  installment_number?: number;
  next_billing_date?: string;
  student_id: string;
  student_name?: string;
}

const ParentBillingDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState<'overview' | 'invoices' | 'payment-plans' | 'payment-methods' | 'payment-history'>('overview');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInvoiceId, setPaymentInvoiceId] = useState<string | null>(null);
  const [paymentInvoiceNumber, setPaymentInvoiceNumber] = useState<string>('');
  const [paymentInvoiceAmount, setPaymentInvoiceAmount] = useState<number>(0);
  const [paymentStudentName, setPaymentStudentName] = useState<string>('');
  const [paymentEventName, setPaymentEventName] = useState<string>('');
  const [paymentPlans, setPaymentPlans] = useState<PaymentPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get user ID helper
  const getUserId = () => {
    return (user as any)?.attributes?.sub || (user as any)?.username || (user as any)?.id || '';
  };

  useEffect(() => {
    const userId = getUserId();
    if (userId) {
      fetchPaymentPlans();
    }
  }, [user]);

  const fetchPaymentPlans = async () => {
    try {
      setLoading(true);
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Get user ID from user attributes or username
      const userId = getUserId();
      if (!userId) {
        setError('User ID not available');
        setLoading(false);
        return;
      }
      const response = await axios.get(
        `${config.BACKEND_URL}/api/billing/payment-plans/parent/${userId}`,
        { headers }
      );
      setPaymentPlans(response.data || []);
    } catch (err: any) {
      console.error('Error fetching payment plans:', err);
      setError(err.response?.data?.detail || 'Failed to load payment plans');
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceClick = (invoiceId: string) => {
    setSelectedInvoiceId(invoiceId);
  };

  const handlePayInvoice = async (invoiceId: string) => {
    try {
      // Fetch invoice to get amount
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(
        `${config.BACKEND_URL}/api/billing/invoices/${invoiceId}`,
        { headers }
      );
      const invoice = response.data;
      setPaymentInvoiceId(invoiceId);
      setPaymentInvoiceNumber(invoice.invoice_number || '');
      setPaymentInvoiceAmount(parseFloat(invoice.balance_due.toString()));
      setPaymentStudentName(invoice.student_name || '');
      setPaymentEventName(invoice.items?.[0]?.description || 'Invoice Payment');
      setShowPaymentModal(true);
    } catch (err: any) {
      console.error('Error fetching invoice:', err);
      alert('Failed to load invoice details');
    }
  };

  const handleViewInvoice = async (invoiceId: string) => {
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Use pdf-url endpoint (similar to consent forms workflow)
      const response = await axios.get(
        `${config.BACKEND_URL}/api/billing/invoices/${invoiceId}/pdf-url`,
        { headers }
      );
      
      if (response.data.success && response.data.pdf_url) {
        window.open(response.data.pdf_url, '_blank', 'noopener,noreferrer');
      } else {
        alert('Failed to load PDF. Please try again.');
      }
    } catch (err: any) {
      console.error('Error viewing invoice:', err);
      alert(err.response?.data?.detail || 'Failed to view invoice');
    }
  };

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Use pdf-url endpoint (similar to consent forms workflow)
      const response = await axios.get(
        `${config.BACKEND_URL}/api/billing/invoices/${invoiceId}/pdf-url`,
        { headers }
      );
      
      if (response.data.success && response.data.pdf_url) {
        window.open(response.data.pdf_url, '_blank', 'noopener,noreferrer');
      } else {
        alert('Failed to load PDF. Please try again.');
      }
    } catch (err: any) {
      console.error('Error downloading invoice:', err);
      alert(err.response?.data?.detail || 'Failed to download invoice');
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setPaymentInvoiceId(null);
    setPaymentInvoiceNumber('');
    setPaymentInvoiceAmount(0);
    setPaymentStudentName('');
    setPaymentEventName('');
    // Refresh data
    fetchPaymentPlans();
  };

  const handleViewPaymentPlan = (planId: string) => {
    // TODO: Show payment plan details modal
    console.log('View payment plan:', planId);
  };

  const handleCancelPaymentPlan = async (planId: string) => {
    if (!window.confirm('Are you sure you want to cancel this payment plan?')) {
      return;
    }
    
    try {
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.delete(
        `${config.BACKEND_URL}/api/billing/payment-plans/${planId}`,
        { headers }
      );
      
      fetchPaymentPlans();
    } catch (err: any) {
      console.error('Error cancelling payment plan:', err);
      alert(err.response?.data?.detail || 'Failed to cancel payment plan');
    }
  };

  const outstandingBalance = paymentPlans
    .filter(plan => plan.plan_status === 'active')
    .reduce((sum, plan) => sum + (plan.monthly_amount || 0), 0);

  return (
    <div className="parent-billing-dashboard">
      <div className="billing-dashboard-header">
        <h2>Billing & Payments</h2>
        <p>Manage your invoices, payment plans, and payment methods</p>
      </div>

      {/* Navigation */}
      <div className="billing-dashboard-nav">
        <button
          className={`billing-nav-tab ${activeSection === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveSection('overview')}
        >
          📊 Overview
        </button>
        <button
          className={`billing-nav-tab ${activeSection === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveSection('invoices')}
        >
          📄 My Invoices
        </button>
        <button
          className={`billing-nav-tab ${activeSection === 'payment-plans' ? 'active' : ''}`}
          onClick={() => setActiveSection('payment-plans')}
        >
          💳 Payment Plans
        </button>
        <button
          className={`billing-nav-tab ${activeSection === 'payment-methods' ? 'active' : ''}`}
          onClick={() => setActiveSection('payment-methods')}
        >
          🏦 Payment Methods
        </button>
        <button
          className={`billing-nav-tab ${activeSection === 'payment-history' ? 'active' : ''}`}
          onClick={() => setActiveSection('payment-history')}
        >
          📜 Payment History
        </button>
      </div>

      {/* Content */}
      <div className="billing-dashboard-content">
        {activeSection === 'overview' && (
          <div className="billing-overview-section">
            <OverdueAlerts />
            
            <div className="billing-overview-stats">
              <div className="overview-stat-card">
                <h3>Outstanding Balance</h3>
                <p className="stat-value">${outstandingBalance.toFixed(2)}</p>
              </div>
              <div className="overview-stat-card">
                <h3>Active Payment Plans</h3>
                <p className="stat-value">{paymentPlans.filter(p => p.plan_status === 'active').length}</p>
              </div>
            </div>

            <div className="billing-overview-recent">
              <h3>Recent Invoices</h3>
              <InvoiceList
                parentId={getUserId()}
                onInvoiceClick={handleInvoiceClick}
                onPayInvoice={handlePayInvoice}
                onViewInvoice={handleViewInvoice}
                onDownloadInvoice={handleDownloadInvoice}
              />
            </div>
          </div>
        )}

        {activeSection === 'invoices' && (
          <div className="billing-invoices-section">
            <InvoiceList
              parentId={(user as any)?.attributes?.sub || (user as any)?.username || (user as any)?.id}
              onInvoiceClick={handleInvoiceClick}
              onPayInvoice={handlePayInvoice}
              onViewInvoice={handleViewInvoice}
              onDownloadInvoice={handleDownloadInvoice}
            />
          </div>
        )}

        {activeSection === 'payment-plans' && (
          <div className="billing-payment-plans-section">
            <h3>Active Payment Plans</h3>
            {loading ? (
              <div className="loading">Loading payment plans...</div>
            ) : error ? (
              <div className="error-message">Error: {error}</div>
            ) : paymentPlans.length === 0 ? (
              <div className="empty-state">
                <p>No active payment plans</p>
              </div>
            ) : (
              <div className="payment-plans-grid">
                {paymentPlans.map(plan => (
                  <PaymentPlanCard
                    key={plan.id}
                    plan={plan}
                    onView={handleViewPaymentPlan}
                    onCancel={handleCancelPaymentPlan}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeSection === 'payment-methods' && (
          <div className="billing-payment-methods-section">
            <PaymentMethodSelector
              onAddNew={() => {
                // TODO: Open Stripe payment method form
                console.log('Add new payment method');
              }}
            />
          </div>
        )}

        {activeSection === 'payment-history' && (
          <div className="billing-payment-history-section">
            <PaymentHistory parentId={getUserId()} />
          </div>
        )}
      </div>

      {/* Invoice Detail Modal */}
      {selectedInvoiceId && (
        <InvoiceDetail
          invoiceId={selectedInvoiceId}
          onClose={() => setSelectedInvoiceId(null)}
          onPay={handlePayInvoice}
          onDownload={handleDownloadInvoice}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && paymentInvoiceId && (
        <StripePayment
          amount={paymentInvoiceAmount}
          description={`Payment for Invoice ${paymentInvoiceNumber || paymentInvoiceId}`}
          metadata={{ invoice_id: paymentInvoiceId, invoice_number: paymentInvoiceNumber, student_name: paymentStudentName }}
          interacContext={{
            invoiceNumber: paymentInvoiceNumber,
            eventName: paymentEventName,
            studentName: paymentStudentName
          }}
          show={showPaymentModal}
          onSuccess={handlePaymentSuccess}
          onInteracSelected={() => {
            alert(
              `Please complete your Interac e-transfer to admin@oarayouthinnovationlab.ca.\n\n` +
              `Message to include:\n${[paymentInvoiceNumber, paymentEventName, paymentStudentName].filter(Boolean).join(' | ')}`
            );
          }}
          onError={(error) => {
            console.error('Payment error:', error);
            alert(`Payment failed: ${error}`);
          }}
          onCancel={() => {
            setShowPaymentModal(false);
            setPaymentInvoiceId(null);
            setPaymentInvoiceNumber('');
            setPaymentInvoiceAmount(0);
            setPaymentStudentName('');
            setPaymentEventName('');
          }}
        />
      )}
    </div>
  );
};

export default ParentBillingDashboard;

